import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import LockIcon from '@material-ui/icons/Lock';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleIcon: {
    marginRight: theme.spacing(1),
  },
  closeButton: {
    color: 'white',
  },
  dialogContent: {
    padding: theme.spacing(3),
    minWidth: 400,
  },
  userInfo: {
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  passwordField: {
    marginBottom: theme.spacing(2),
  },
  passwordRequirements: {
    backgroundColor: '#e3f2fd',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  requirement: {
    fontSize: '0.875rem',
    color: '#1976d2',
    marginBottom: theme.spacing(0.5),
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: '#fafafa',
  },
  cancelButton: {
    marginRight: theme.spacing(1),
  },
  saveButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
}));

const CambiarPasswordDialog = ({ open, onClose, user, onChangePassword }) => {
  const classes = useStyles();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Limpiar estados cuando se abre el modal
  useEffect(() => {
    if (open) {
      setPassword('');
      setConfirmPassword('');
      setErrors({});
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [open]);

  const validatePassword = (pwd) => {
    const errors = {};

    if (!pwd || pwd.trim() === '') {
      errors.password = 'La contraseña es requerida';
    } else if (pwd.trim().length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
    } else if (pwd.trim() === user?.username) {
      errors.password = 'La contraseña no puede ser igual al nombre de usuario';
    }

    return errors;
  };

  const validateConfirmPassword = (pwd, confirmPwd, showErrorsOnlyWhenComplete = false) => {
    const errors = {};

    if (!confirmPwd) {
      // Solo mostrar error si no está escribiendo o si se solicita validación completa
      if (!showErrorsOnlyWhenComplete || confirmPwd === '') {
        errors.confirmPassword = 'Debe confirmar la contraseña';
      }
    } else if (pwd !== confirmPwd) {
      // Solo mostrar error si ambas contraseñas tienen contenido significativo
      // o si se solicita validación completa
      if (showErrorsOnlyWhenComplete || (pwd.length >= 6 && confirmPwd.length >= 6)) {
        console.log('Contraseña original:', JSON.stringify(pwd));
        console.log('Contraseña confirmación:', JSON.stringify(confirmPwd));
        console.log('Son iguales?', pwd === confirmPwd);
        console.log('Longitud original:', pwd.length);
        console.log('Longitud confirmación:', confirmPwd.length);
        errors.confirmPassword = 'Las contraseñas no coinciden';
      }
    } else {
      // Las contraseñas son iguales - limpiar cualquier error previo
      console.log('✅ Contraseñas iguales - limpiando errores');
    }

    return errors;
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    // Validación simple y directa
    const errors = {};

    // Validar contraseña principal
    if (!newPassword) {
      errors.password = 'La contraseña es requerida';
    } else if (newPassword.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    // Validar confirmación si existe
    if (confirmPassword && newPassword !== confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(errors);
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);

    // Validación simple y directa
    const errors = {};

    // Validar contraseña principal
    if (!password) {
      errors.password = 'La contraseña es requerida';
    } else if (password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    // Validar confirmación
    if (!newConfirmPassword) {
      errors.confirmPassword = 'Debe confirmar la contraseña';
    } else if (password !== newConfirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(errors);
  };

  const handleSubmit = () => {
    // Validación final simple
    const errors = {};

    if (!password) {
      errors.password = 'La contraseña es requerida';
    } else if (password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Debe confirmar la contraseña';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(errors);

    // Si no hay errores, proceder
    if (Object.keys(errors).length === 0) {
      onChangePassword(password);
      handleClose();
    }
  };

  const handleClose = () => {
    setPassword('');
    setConfirmPassword('');
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center">
          <LockIcon className={classes.titleIcon} />
          <Typography variant="h6">
            Cambiar Contraseña
          </Typography>
        </Box>
        <IconButton className={classes.closeButton} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Información del usuario */}
        <Box className={classes.userInfo}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
            Usuario: {user?.username}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {user?.nombres} {user?.apellidos}
          </Typography>
        </Box>

        {/* Campo Nueva Contraseña */}
        <TextField
          fullWidth
          label="Nueva Contraseña"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={handlePasswordChange}
          error={!!errors.password}
          helperText={errors.password}
          className={classes.passwordField}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={togglePasswordVisibility} edge="end">
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Campo Confirmar Contraseña */}
        <TextField
          fullWidth
          label="Confirmar Nueva Contraseña"
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={handleConfirmPasswordChange}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          className={classes.passwordField}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={toggleConfirmPasswordVisibility} edge="end">
                  {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Requisitos de contraseña */}
        <Box className={classes.passwordRequirements}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
            Requisitos de la contraseña:
          </Typography>
          <Typography className={classes.requirement}>
            • Mínimo 6 caracteres
          </Typography>
          <Typography className={classes.requirement}>
            • No puede ser igual al nombre de usuario
          </Typography>
          <Typography className={classes.requirement}>
            • Se recomienda usar letras, números y símbolos
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleClose}
          className={classes.cancelButton}
          variant="outlined"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          className={classes.saveButton}
          variant="contained"
          disabled={!password || !confirmPassword || Object.keys(errors).length > 0}
        >
          Cambiar Contraseña
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CambiarPasswordDialog;
